/**
 * Centralized Tool Registry for CoRegulate
 * 
 * All regulation tools are defined here once and referenced by ID across all paths.
 * This ensures consistency and makes it easy to update tools globally.
 */

// =============================================================================
// TOOL TYPES
// =============================================================================
export const TOOL_TYPES = {
    BREATH: 'breath',
    GROUNDING: 'grounding',
    BLS: 'bls',
    VISUAL_BLS: 'visual_bls',
    ANCHORING: 'anchoring',
    REFLECTION: 'reflection',
    CLOSING: 'closing',
    SOMATIC: 'somatic',
};

// =============================================================================
// BREATH TOOLS
// =============================================================================
export const BREATH_TOOLS = {
    chest_breath: {
        id: 'chest_breath',
        type: TOOL_TYPES.BREATH,
        name: 'Chest Breathing',
        instruction: 'Place both hands on your chest. Breathe into your own touch. Let your exhale be a soft sigh.',
        duration: 11,
        variants: {
            short: { duration: 8 },
            extended: { duration: 15 },
        },
    },
    belly_breath: {
        id: 'belly_breath',
        type: TOOL_TYPES.BREATH,
        name: 'Belly Breathing',
        instruction: 'Place one hand on your belly. Take a soft breath.',
        duration: 11,
        variants: {
            short: { duration: 8 },
            extended: { duration: 15 },
        },
    },
    shoulder_drop_breath: {
        id: 'shoulder_drop_breath',
        type: TOOL_TYPES.BREATH,
        name: 'Shoulder Release',
        instruction: 'Let your shoulders drop. There\'s no rush.',
        duration: 11,
        variants: {
            short: { duration: 8 },
        },
    },
    long_exhale: {
        id: 'long_exhale',
        type: TOOL_TYPES.BREATH,
        name: 'Extended Exhale',
        instruction: 'Breathe out longer than you breathe in.',
        duration: 11,
        variants: {
            short: { duration: 8 },
            extended: { duration: 15 },
        },
    },
    warmth_breath: {
        id: 'warmth_breath',
        type: TOOL_TYPES.BREATH,
        name: 'Warmth Breathing',
        instruction: 'Place a hand on your chest. Breathe into the warmth.',
        duration: 11,
        variants: {
            short: { duration: 8 },
        },
    },
};

// =============================================================================
// GROUNDING TOOLS
// =============================================================================
export const GROUNDING_TOOLS = {
    feet_grounding: {
        id: 'feet_grounding',
        type: TOOL_TYPES.GROUNDING,
        name: 'Feet Grounding',
        instruction: 'Press your feet into the floor. Feel the ground.',
        duration: 10,
        variants: {
            short: { duration: 7 },
            extended: { duration: 15 },
        },
    },
    hand_awareness: {
        id: 'hand_awareness',
        type: TOOL_TYPES.GROUNDING,
        name: 'Hand Awareness',
        instruction: 'Feel your hands. Nothing else matters right now.',
        duration: 10,
        variants: {
            short: { duration: 7 },
        },
    },
    color_notice: {
        id: 'color_notice',
        type: TOOL_TYPES.GROUNDING,
        name: 'Color Noticing',
        instruction: 'Pick one thing you can see. Notice its color.',
        duration: 10,
        variants: {
            short: { duration: 7 },
            extended: { duration: 15 },
        },
    },
    finger_wiggle: {
        id: 'finger_wiggle',
        type: TOOL_TYPES.SOMATIC,
        name: 'Finger Movement',
        instruction: 'Wiggle your fingers slowly. Just notice the movement.',
        duration: 10,
        variants: {
            short: { duration: 7 },
        },
    },
};

// =============================================================================
// BLS (Bilateral Stimulation) TOOLS
// =============================================================================
export const BLS_TOOLS = {
    knee_tapping: {
        id: 'knee_tapping',
        type: TOOL_TYPES.BLS,
        name: 'Knee Tapping',
        instruction: 'Tap your knees gently, left then right. Slow and steady.',
        duration: 15,
        variants: {
            short: { duration: 10 },
            extended: { duration: 20 },
        },
    },
    butterfly_hug: {
        id: 'butterfly_hug',
        type: TOOL_TYPES.BLS,
        name: 'Butterfly Hug',
        instruction: 'Cross your arms and give yourself a gentle hug. Tap slowly: left, right.',
        duration: 15,
        variants: {
            short: { duration: 10 },
            extended: { duration: 20 },
        },
    },
    visual_bls: {
        id: 'visual_bls',
        type: TOOL_TYPES.VISUAL_BLS,
        name: 'Eye Movement',
        instruction: 'Follow the light with your eyes. Let the rhythm settle you.',
        duration: 9,
        cycles: 3,
        speed: 3,
        variants: {
            slow: { duration: 15, cycles: 3, speed: 5 },
            fast: { duration: 6, cycles: 3, speed: 2 },
        },
    },
};

// =============================================================================
// ANCHORING TOOLS
// =============================================================================
export const ANCHORING_TOOLS = {
    safe_place: {
        id: 'safe_place',
        type: TOOL_TYPES.ANCHORING,
        name: 'Safe Place Visualization',
        instruction: 'Close your eyes and think of a time where you were completely at peace, grounded and calm. Let your mind take you back to this time. In your mind\'s eye, look around — what can you see? What can you smell? What can you hear?',
        duration: 0, // No timer - user controls pace
        variants: {},
    },
};

// =============================================================================
// REFLECTION TOOLS
// =============================================================================
export const REFLECTION_TOOLS = {
    emotion_notice: {
        id: 'emotion_notice',
        type: TOOL_TYPES.REFLECTION,
        name: 'Emotion Noticing',
        instruction: 'You don\'t have to do anything with this feeling. Just notice how it shifts and changes.',
        duration: 0, // No timer - optional reflection
        optional: true,
        variants: {},
    },
};

// =============================================================================
// CLOSING TOOLS
// =============================================================================
export const CLOSING_TOOLS = {
    closing_frozen: {
        id: 'closing_frozen',
        type: TOOL_TYPES.CLOSING,
        name: 'Frozen Closing',
        instruction: 'You\'re here. You\'re okay. This will pass.',
        duration: 0,
        variants: {},
    },
    closing_scattered: {
        id: 'closing_scattered',
        type: TOOL_TYPES.CLOSING,
        name: 'Scattered Closing',
        instruction: 'Right now, you only need to do this.',
        duration: 0,
        variants: {},
    },
    closing_flooded: {
        id: 'closing_flooded',
        type: TOOL_TYPES.CLOSING,
        name: 'Flooded Closing',
        instruction: 'You are more than this moment. This feeling will shift.',
        duration: 0,
        variants: {},
    },
    closing_generic: {
        id: 'closing_generic',
        type: TOOL_TYPES.CLOSING,
        name: 'Gentle Closing',
        instruction: 'Take a moment. You did something kind for yourself.',
        duration: 0,
        variants: {},
    },
};

// =============================================================================
// COMBINED TOOL REGISTRY
// =============================================================================
export const TOOLS = {
    ...BREATH_TOOLS,
    ...GROUNDING_TOOLS,
    ...BLS_TOOLS,
    ...ANCHORING_TOOLS,
    ...REFLECTION_TOOLS,
    ...CLOSING_TOOLS,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a tool by ID
 * @param {string} toolId - The tool ID
 * @returns {object|null} - The tool object or null if not found
 */
export const getTool = (toolId) => {
    return TOOLS[toolId] || null;
};

/**
 * Get a tool with a specific variant applied
 * @param {string} toolId - The tool ID
 * @param {string} variantName - The variant name (e.g., 'short', 'extended')
 * @returns {object|null} - The tool with variant properties merged
 */
export const getToolWithVariant = (toolId, variantName) => {
    const tool = TOOLS[toolId];
    if (!tool) return null;
    
    const variant = tool.variants?.[variantName];
    if (!variant) return tool;
    
    return {
        ...tool,
        ...variant,
        _variant: variantName,
    };
};

/**
 * Get all tools of a specific type
 * @param {string} type - The tool type from TOOL_TYPES
 * @returns {object[]} - Array of tools matching the type
 */
export const getToolsByType = (type) => {
    return Object.values(TOOLS).filter(tool => tool.type === type);
};

/**
 * Build a step object from a tool ID (for use in paths)
 * @param {string} toolId - The tool ID
 * @param {string} variantName - Optional variant name
 * @returns {object} - Step object compatible with OverwhelmNavigator
 */
export const buildStep = (toolId, variantName = null) => {
    const tool = variantName ? getToolWithVariant(toolId, variantName) : getTool(toolId);
    if (!tool) {
        console.warn(`Tool not found: ${toolId}`);
        return null;
    }
    
    return {
        toolId: tool.id,
        text: tool.instruction,
        duration: tool.duration,
        isBreath: tool.type === TOOL_TYPES.BREATH,
        isGrounding: tool.type === TOOL_TYPES.GROUNDING,
        isBLS: tool.type === TOOL_TYPES.BLS,
        isVisualBLS: tool.type === TOOL_TYPES.VISUAL_BLS,
        isAnchoring: tool.type === TOOL_TYPES.ANCHORING,
        isReflection: tool.type === TOOL_TYPES.REFLECTION,
        isClosing: tool.type === TOOL_TYPES.CLOSING,
        isSomatic: tool.type === TOOL_TYPES.SOMATIC,
        optional: tool.optional || false,
        cycles: tool.cycles,
        speed: tool.speed,
        _variant: tool._variant,
    };
};

/**
 * Build multiple steps from an array of tool configs
 * @param {Array<string|{id: string, variant?: string}>} toolConfigs - Array of tool IDs or config objects
 * @returns {object[]} - Array of step objects
 */
export const buildSteps = (toolConfigs) => {
    return toolConfigs
        .map(config => {
            if (typeof config === 'string') {
                return buildStep(config);
            }
            return buildStep(config.id, config.variant);
        })
        .filter(Boolean);
};

// =============================================================================
// TOOL METADATA (for UI display)
// =============================================================================
export const TOOL_TYPE_META = {
    [TOOL_TYPES.BREATH]: {
        label: 'Breath',
        color: '#1D6B6B',
        bgColor: '#7D8E95',
    },
    [TOOL_TYPES.GROUNDING]: {
        label: 'Grounding',
        color: '#7D8E95',
        bgColor: '#7D8E95',
    },
    [TOOL_TYPES.BLS]: {
        label: 'Tapping',
        color: '#7D8E95',
        bgColor: '#7D8E95',
    },
    [TOOL_TYPES.VISUAL_BLS]: {
        label: 'Eye Movement',
        color: '#7D8E95',
        bgColor: '#7D8E95',
    },
    [TOOL_TYPES.ANCHORING]: {
        label: 'Anchoring',
        color: '#7D8E95',
        bgColor: '#E2D5E8',
    },
    [TOOL_TYPES.REFLECTION]: {
        label: 'Optional Reflection',
        color: '#7D8E95',
        bgColor: '#FFBB98',
    },
    [TOOL_TYPES.CLOSING]: {
        label: 'Closing',
        color: '#1D6B6B',
        bgColor: '#7D8E95',
    },
    [TOOL_TYPES.SOMATIC]: {
        label: 'Body Awareness',
        color: '#7D8E95',
        bgColor: '#7D8E95',
    },
};
